import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { Shell } from './components/Shell';
import { AdminLogin, LoginPicker, OrgLogin } from './routes/Login';
import { EnterOrganization } from './routes/EnterOrganization';
import { Classes } from './routes/Classes';
import { Grid } from './routes/Grid';
import { Reports } from './routes/Reports';
import { ManageStudents } from './routes/ManageStudents';
import { ManageStudentDetail } from './routes/ManageStudentDetail';
import { ManageUsers } from './routes/ManageUsers';
import { ManageUserDetail } from './routes/ManageUserDetail';
import { ManageCalendar } from './routes/ManageCalendar';
import { ManageClasses } from './routes/ManageClasses';
import { ManageClassDetail } from './routes/ManageClassDetail';
import { ManagePeriodDetail } from './routes/ManagePeriodDetail';
import { Platform } from './routes/Platform';
import { PlatformPeople } from './routes/PlatformPeople';
import { Setup } from './routes/Setup';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

/*
 * Search parameters are whatever is in the address bar, so every one of these
 * is optional and every absent value means the page's own default. Two things
 * follow: an untouched list has a completely clean URL, and clearing a control
 * removes its parameter rather than writing an explicit "off" that the next
 * reader has to interpret. `all` is the absence of a filter, not a value.
 *
 * Hand written rather than zod, matching the register's `date` below. These are
 * a dozen optional strings, and a `validateSearch` that throws would land on a
 * route error boundary this app does not have.
 */
type Search = Record<string, unknown>;

const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;

/**
 * Every ruled list carries these two.
 *
 * The return types below are annotated with OPTIONAL properties rather than
 * inferred. Inference gives `{ q: string | undefined }`, whose key is still
 * required, and that makes `search` a required prop on every `<Link>` pointing
 * at the route.
 */
interface ListSearch {
  q?: string;
  sort?: string;
  /**
   * Which stage of a record's life the list is showing. Absent is the live
   * ones, which is why an untouched list has a clean address.
   *
   * `deleted` resolves for anybody, because a search parameter is whatever
   * somebody typed. What it shows is decided by the server, which returns
   * deleted rows only to a platform administrator, so an ordinary
   * administrator visiting the link simply sees an empty tab rather than a
   * different answer.
   */
  show?: 'inactive' | 'deleted';
}

const listSearch = (s: Search): ListSearch => ({
  q: str(s.q),
  sort: str(s.sort),
  show: oneOf(s.show, ['inactive', 'deleted'] as const),
});

const orgLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/o/$slug/login',
  component: OrgLogin,
});

const adminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/login',
  component: AdminLogin,
});

const loginPickerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPicker,
});

/**
 * Outside the shell: whoever follows a setup link is not signed in yet, so it
 * must not sit under the layout route that redirects on a 401.
 */
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup/$token',
  component: Setup,
});

/**
 * Also outside the shell, though its visitor is signed in: a platform
 * administrator choosing which organization to work inside. The shell's
 * masthead and tabs describe a scope, and this page is where that scope is
 * still being decided.
 */
const enterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/enter',
  component: EnterOrganization,
});

/** Everything below here sits inside the signed-in shell. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: Shell,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: Classes,
});

const classesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/classes',
  component: Classes,
});

const gridRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/classes/$classId',
  validateSearch: (s: Search): { date?: string } => ({ date: str(s.date) }),
  component: Grid,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  validateSearch: (
    s: Search,
  ): ListSearch & { from?: string; to?: string; by?: 'student' | 'class' | 'period' | 'date' } => ({
    ...listSearch(s),
    from: str(s.from),
    to: str(s.to),
    by: oneOf(s.by, ['student', 'class', 'period', 'date'] as const),
  }),
  component: Reports,
});

const studentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/manage/students',
  validateSearch: listSearch,
  component: ManageStudents,
});

/**
 * One student, with the classes they are on the roll of. No search parameters:
 * its one list is short enough to read whole, and the filters that earn a place
 * in the address belong to the tables that can run to hundreds of rows.
 */
const studentDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/manage/students/$studentId',
  component: ManageStudentDetail,
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/manage/users',
  validateSearch: listSearch,
  component: ManageUsers,
});

/** One staff record, with the classes they take. */
const userDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/manage/users/$userId',
  component: ManageUserDetail,
});

const manageClassesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/manage/classes',
  /*
   * The one route with two lists, so neither gets the bare names. Both tables
   * declare a column called "name", and sharing `sort` would make a click on
   * one sort the other and desynchronise the click cycle.
   */
  validateSearch: (
    s: Search,
  ): {
    class_q?: string;
    class_sort?: string;
    class_show?: 'inactive' | 'deleted';
    period_sort?: string;
    period_show?: 'inactive' | 'deleted';
  } => ({
    class_q: str(s.class_q),
    class_sort: str(s.class_sort),
    class_show: oneOf(s.class_show, ['inactive', 'deleted'] as const),
    // No period_q: that list has no search box, and a parameter nothing
    // reads is worse than one that cannot exist.
    period_sort: str(s.period_sort),
    period_show: oneOf(s.period_show, ['inactive', 'deleted'] as const),
  }),
  component: ManageClasses,
});

const manageClassDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/manage/classes/$classId',
  component: ManageClassDetail,
});

/**
 * One period, with the classes whose register it is a column of. Sits under
 * /manage/periods even though the list lives on the Classes tab, because a
 * period is its own noun and /manage/classes/$classId already means a class.
 */
const periodDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/manage/periods/$periodId',
  component: ManagePeriodDetail,
});

const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/manage/calendar',
  validateSearch: (s: Search): ListSearch & { from?: string; to?: string } => ({
    ...listSearch(s),
    from: str(s.from),
    to: str(s.to),
  }),
  component: ManageCalendar,
});

const platformRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform',
  validateSearch: listSearch,
  component: Platform,
});

const platformPeopleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/people',
  validateSearch: (s: Search): ListSearch & { org?: string } => ({
    ...listSearch(s),
    org: str(s.org),
  }),
  component: PlatformPeople,
});

const routeTree = rootRoute.addChildren([
  orgLoginRoute,
  adminLoginRoute,
  loginPickerRoute,
  setupRoute,
  enterRoute,
  appRoute.addChildren([
    indexRoute,
    classesRoute,
    gridRoute,
    reportsRoute,
    studentsRoute,
    studentDetailRoute,
    usersRoute,
    userDetailRoute,
    manageClassesRoute,
    manageClassDetailRoute,
    periodDetailRoute,
    calendarRoute,
    platformRoute,
    platformPeopleRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
